import { z } from 'zod';

/**
 * The MARK of a replay on a session (docs/specs/rejeu.md sections 11.3 and 12,
 * issue #193): the enumerations the mark is made of, the block of a session's
 * detail and its heavy layer.
 *
 * A file of its own, importing nothing but Zod, because the list row and the
 * detail of a session (`session-play.ts`, `session-read.ts`) carry the mark
 * while `replay.ts` itself imports `session-play.ts` for the advance of the
 * person: one file holding both would be a cycle, and a cycle of ESM modules
 * reads a schema before it exists.
 */

/* ---------------------------------------------------------- the enumerations */

/**
 * The fidelity of the player, chosen by the operator at the start and kept on
 * the session (spec section 10.2, D7): `strict` says nothing the source does
 * not hold, `completed` may add plausible details and never contradicts it.
 */
export const replayModeSchema = z.enum(['strict', 'completed']);
export type ReplayMode = z.infer<typeof replayModeSchema>;

/** What a source is (spec section 5, D5): a written text, a session's transcript, an interview. */
export const replaySourceKindSchema = z.enum(['written', 'session', 'interview']);
export type ReplaySourceKind = z.infer<typeof replaySourceKindSchema>;

/**
 * The mark of a replay on a line of the list of sessions (spec section 11.3,
 * section 12): `{ mode, sourceKind, sourceLabel }`, or null on an ordinary
 * session. Readable wherever the session is, even once the replay is closed.
 */
export const replayMarkSchema = z
  .object({
    mode: replayModeSchema,
    sourceKind: replaySourceKindSchema,
    sourceLabel: z.string().min(1),
  })
  .strict();
export type ReplayMark = z.infer<typeof replayMarkSchema>;

/** The filter `replay` of the list: the replays only, or the sessions without them. */
export const replayFilterSchema = z.enum(['with', 'without']);
export type ReplayFilter = z.infer<typeof replayFilterSchema>;

/**
 * The fingerprint of the two paragraphs of rules a replay was served (spec
 * section 10.1): the first 8 hexadecimal characters of the SHA-256 of
 * `replayFingerprintInput`, at the régime of `noyauPromptFingerprintSchema`.
 * Computed by the server, which alone holds a hash function.
 */
export const replayFingerprintSchema = z.string().regex(/^[0-9a-f]{8}$/);
export type ReplayFingerprint = z.infer<typeof replayFingerprintSchema>;

/**
 * The origin of a replayed replay (`fromReplay`): its identifier, and the number
 * of the experience version it played, null when that replay is gone (purged
 * with its session, or wiped by a refresh of the dev model).
 */
export const replayOriginSchema = z
  .object({
    replayId: z.string().min(1),
    experienceVersionNumber: z.number().int().positive().nullable(),
  })
  .strict();
export type ReplayOrigin = z.infer<typeof replayOriginSchema>;

/**
 * The block `replay` of a session's detail (spec section 11.3, section 12):
 * everything but the two heavy texts. The shape `get_session` serves by default.
 *
 * `operator` is who started it (the email its `versionAuthor` signs), `playerModel`
 * the model Claude declared (never verified), `sourceChars` the size of the
 * source, `startedAt` the instant of the start.
 */
export const sessionReplaySchema = z
  .object({
    mode: replayModeSchema,
    sourceKind: replaySourceKindSchema,
    sourceLabel: z.string().min(1),
    sourceChars: z.number().int().nonnegative(),
    operator: z.string().min(1),
    playerModel: z.string().min(1).nullable(),
    instructionFingerprint: replayFingerprintSchema,
    fromReplay: replayOriginSchema.nullable(),
    startedAt: z.string().datetime(),
  })
  .strict();
export type SessionReplay = z.infer<typeof sessionReplaySchema>;

/**
 * The HEAVY layer of a replay (spec section 11.3): the text of the source and
 * the instruction as served, behind the layer `replay` of `get_session` so the
 * default reading stays light.
 */
export const sessionReplayLayerSchema = z
  .object({
    sourceText: z.string(),
    instruction: z.string(),
  })
  .strict();
export type SessionReplayLayer = z.infer<typeof sessionReplayLayerSchema>;

/**
 * The block entire - the light facts and the heavy layer - as the screen of a
 * session and the JSON export read it (spec section 12: « le JSON porte le bloc
 * `replay` entier, source et consigne comprises »).
 */
export const sessionReplayFullSchema = sessionReplaySchema.merge(sessionReplayLayerSchema);
export type SessionReplayFull = z.infer<typeof sessionReplayFullSchema>;
